/**
 * ScoreGauge — circular gauge displaying the SmartBlock efficiency score [0, 1].
 * Renders as a half-circle SVG arc with color gradient from red → amber → emerald.
 */
import React from 'react';

type ScoreGaugeProps = {
  score: number; // 0–1
  label?: string;
  size?: number;
};

function scoreColor(score: number): string {
  if (score >= 0.7) return '#059669'; // emerald-600
  if (score >= 0.45) return '#d97706'; // amber-600
  return '#dc2626'; // red-600
}

export function ScoreGauge({ score, label = 'Efficiency', size = 160 }: ScoreGaugeProps) {
  const clamped = Math.min(Math.max(score, 0), 1);
  const radius = (size - 20) / 2;
  const cx = size / 2;
  const cy = size / 2 + 10;
  const circumference = Math.PI * radius; // half-circle
  const dashOffset = circumference * (1 - clamped);
  const color = scoreColor(clamped);
  const percentage = Math.round(clamped * 100);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size / 2 + 30} viewBox={`0 0 ${size} ${size / 2 + 30}`}>
        {/* Background arc */}
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={10}
          strokeLinecap="round"
        />
        {/* Score arc */}
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none"
          stroke={color}
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className="transition-all duration-700 ease-out"
        />
        {/* Score text */}
        <text
          x={cx}
          y={cy - 14}
          textAnchor="middle"
          className="fill-cocoa text-3xl font-bold"
          fontSize={28}
        >
          {percentage}
        </text>
        <text
          x={cx}
          y={cy + 6}
          textAnchor="middle"
          className="fill-cocoa/60 text-xs"
          fontSize={12}
        >
          {label}
        </text>
      </svg>
    </div>
  );
}

export default ScoreGauge;
